/* eslint-disable jsx-a11y/anchor-is-valid */
import { QS_CONFIG } from '../common/Utilities'
import axios from 'axios'
import { useDispatch } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'
import qs from 'query-string'
import { identity } from 'lodash'
import { useQuery  } from 'react-query'

// don't allow per_page values above this limit
const MAX_PER_PAGE = 60
const valid_per_page = per_page => !isNaN(per_page) && per_page <= MAX_PER_PAGE
const cacheTime = process.env.REACT_APP_CACHE_TIME || 1000 * 60 * 10;


export const REQUIRED_FIELD_MESSAGE = 'Faltan campos requeridos'
const emptyQueryParamsDefaults = { page: 1, per_page: 10 }

/**Utility Hook for fetching. it does mainly querystring update, synchronization
 * and parsing, pending/error states, and some utilities methods for updating
 * the querystring that are optional.  meaning that the querystring can be
 * updated without those methods (eg. URLSearchParams) and it will still work
 * the browser's url querystring is the only source of truth
 * it also mantains the location.state providing a way to access full objects
 * but flattening the relevant param in the query string. example:
 * brand_id: { label: "acura": value: "123456" }
 * ...&brand_id=123456&per_page=10&page=1&...
 * @param {Function} BackendService  an async function or promise that returns data
 * @param {Object} QueryParamsDefaults  default values for the filters.
 *          any value can be a function that returns the value
 *          except the filters "page" and "per_page"
 *          eg: {page:1, per_page: 10, columns: () => getColumnsFromStorage()}
 * @param {string} Module A string for logging purposes. eg: "Maestro"
 * @param {array[{name, label}]} RequiredFields values required in the query parameters for fetching data
 * @param {Function} Transform callback to change the backend response
 * @param {Object} QueryParser config object for query string parsing
 */
export function usePaginatedList({
  BackendService,
  RequiredFields,
  QueryParamsDefaults = emptyQueryParamsDefaults,
  Transform = identity,
  Dependencies,
  QueryParser = QS_CONFIG,
  disableQueryCache,
  StopQuery = false,
  retry
}) {
  const location = useLocation()
  const dispatch = useDispatch()
  const history = useHistory()
  const search = new URLSearchParams(location.search)
  const requiredFieldsMissing = RequiredFields?.filter(
    field => !search.has(field.name)
  ).map(field => field.label)
  const hasRequiredFieldsMissing =
    RequiredFields && requiredFieldsMissing?.length > 0
  if (!location.search) {
    // querystring initialization.
    history.replace({
      search: '?' + qs.stringify(QueryParamsDefaults, QueryParser),
      state: {...location.state, ...QueryParamsDefaults}
    })
  }

  const {
    data,
    error,
    isFetching,
    isLoading,
    remove,
    refetch,
    isPreviousData
  } = useQuery(
    [BackendService.name, location.search],
    async () => {
      const source = axios.CancelToken.source()
      const promise = BackendService({
        search: location.search,
        cancelToken: source.token
      }).then(Transform)
      promise.cancel = () => {
        source.cancel('Query was cancelled by React Query')
      }
      return promise
    },
    {
      keepPreviousData: !hasRequiredFieldsMissing,
      enabled: Boolean(location.search) && !hasRequiredFieldsMissing && !StopQuery,
      cacheTime: disableQueryCache ? 0 : cacheTime,
      ...(retry !== undefined ? { retry } : {}),
    }
  )
  let currState
  // **************************** FILTER HANDLERS ****************************
  // this is just a parser to parse the filter object to a querystring
  const _buildNewQuery = obj => {
    if(!location.state) {
      const parsedSearch = qs.parse(location.search,QueryParser)
      location.state = { ...QueryParamsDefaults,...parsedSearch, ...obj}
    }
    currState = history.location.state
    if (currState?.breadcrumbs){
      delete currState?.breadcrumbs
    }
    // bail if there isn't an object
    if (!obj) return console.warn('_buildNewQuery called with null argument')
    // bail if there isn't a location.state -- state not initialized
  if (!currState)
      return console.warn('_buildNewQuery called before state initialization')

    const newLocationState = { ...currState, ...obj }
    
    // reset the page to "1" if it's not included in "obj" to avoid fetching
    // a page that it's out of range. page 1 is always on range
    if (!obj.hasOwnProperty('page') && currState.page) {
      newLocationState.page = '1'
    }
    
    // check and clean dependencies
    // for the props changed, delete the keys that depend on that prop
    // example: { brand_id: ['model_id', 'version_id'] }.
    // this "Dependencies" object will remove model_id and version_id
    // but only if the "obj" has the prop "brand_id" and this prop changes
    // from the previous value
    if (Dependencies) {
      Object.keys(Dependencies)
      .filter(key => obj.hasOwnProperty(key) && obj[key] !== currState[key])
      .forEach(key => {
        Dependencies[key].forEach(keyDependency => {
          delete newLocationState[keyDependency]
        })
      })
    }

    const newQuery = { ...newLocationState }
    Object.keys(newQuery).forEach(key => {
      // flatten react-select objects with the shape { label, value }
      if (newQuery[key]?.value) {
        newQuery[key] = newQuery[key].value
      }
      // flatten objects with the shape { id, ...otherProps }
      if (newQuery[key]?.id) {
        newQuery[key] = newQuery[key].id
      }
      // ignore fields starting with '__'
      // these can be ui states that don't belong in the querystring
      if (key.startsWith('__')) delete newQuery[key]
      // remove empty params
      if (newQuery[key]?.length === 0) delete newQuery[key]
      if (Array.isArray(newQuery[key])){
        if(key+'[]' in newQuery)delete newQuery[key]
      }
    })

    const newLocation = {
      search: '?' + qs.stringify(newQuery, QueryParser),
      hash: location.hash,
      state: newLocationState
    }
    history.replace(newLocation)
  }

  const onChangePage = page => {
    _buildNewQuery({ page })
  }

  const onChangePerPage = option => {
    const per_page = valid_per_page(option?.value)
      ? option.value
      : QueryParamsDefaults.per_page
    _buildNewQuery({ per_page })
  }

  const _changeOneValue = ({ name, value, toggle }) => {
    const currQuery = qs.parse(location.search, QueryParser)
    const prevValue = currQuery[name]
    // if it's the same value and toggle flag is specified, remove the value
    if (prevValue === value && toggle === true) {
      _buildNewQuery({ [name]: undefined })
    } else {
      _buildNewQuery({ [name]: value })
    }
  }
  
  const onChangeFiltros = obj => {
    if (obj && obj.hasOwnProperty('name') && obj.hasOwnProperty('value')) {
      _changeOneValue(obj)
    } else {
      _buildNewQuery(obj)
    }
  }

  return {
    // an array of objects: [{},{},{},{}]
    results: data,
    // an object with the querystring from the url parsed
    // FIXME: change to location state and refactor async select where the hook is used if necessary
    filters: qs.parse(location.search, QueryParser),
    // a function to change the filters object and the querystring
    onChangeFiltros,
    // error state
    error: error,
    // pending state
    pending: (isLoading || isPreviousData) && !hasRequiredFieldsMissing,
    // background pending state
    pendingBackground: !isLoading && !isPreviousData && isFetching,
    requiredFieldsMissing,

    // --[ OPTIONAL UTILITIES]--
    // callback to change per page
    onChangePerPage,
    // callback to change page
    onChangePage,
    // useLocation hook for reutilization
    location,
    // useDispatch hook for reutilization
    dispatch,
    // useHistory hook for reutilization
    history,
    // a function for imperatively force a refetch of data
    forceUpdate: () => {
      remove()
      refetch()
    }
  }
}
  
